import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHeaders, HttpInterceptorFn, HttpParams, HttpRequest } from '@angular/common/http';
import { SpinnerService } from '@nesea/ngx-ui-kit/spinner';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { ErrorService } from '@core/services/error.service';
import { AuthService } from '@core/services/auth.service';
import { CoreStore } from '@core/store/core.store';
import { accessEvents, authPageEvents, sessionEvents } from '@core/store/core.events';
import { injectDispatch } from '@ngrx/signals/events';
import { createLoginError, ITokenData, SESSION_EXPIRED_ERROR } from '@core/store/core.models';
import { NServicesHttpErrorResponse } from '@core/models/custom-http-error.model';
import { ErrorCodeEnum } from '@core/enums/error-code.enum';
import { ConfigService } from '@core/services/config.service';
import { IConfig } from '@core/models/interfaces/config.interface';

export const SKIP_TOKEN = new HttpContextToken(() => false);

let isRefreshTokenInProgress: boolean = false;
const refreshToken$: BehaviorSubject<ITokenData | null> = new BehaviorSubject<ITokenData | null>(null);

const JSON_PATTERN: RegExp = /\.json$/i;

const AUTHORIZATION: string = 'Authorization';
const ACCESS_TOKEN: string = 'Access-token';
const USERNAME: string = 'username';

export const coreInterceptor: HttpInterceptorFn = (req, next) => {
  const configService: ConfigService = inject(ConfigService);
  const spinnerService: SpinnerService = inject(SpinnerService);
  const errorService: ErrorService = inject(ErrorService);
  const authService: AuthService = inject(AuthService);
  const coreStore = inject(CoreStore);
  const sessionDispatch = injectDispatch(sessionEvents);
  const accessDispatch = injectDispatch(accessEvents);
  const authPageDispatch = injectDispatch(authPageEvents);

  spinnerService.show();

  // Synchronous read: HttpClient requests are cold, so the interceptor body runs at
  // subscribe time, exactly as store.select(...).pipe(take(1)) used to.
  const { email, idToken, accessToken } = coreStore.sessionData();
  req = buildRequest(req, configService.config, email, idToken, accessToken);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => isBlobError(err) ? parseBlobError(err) : throwError(() => err)),
    catchError((err: HttpErrorResponse) => {
      // generateToken or refreshToken
      if(req.url.includes(authService.tokenEndpoint)) {
        // The login page is the error surface: no toast, the error travels with the event.
        const grantType: string | null = (req.body as HttpParams | null)?.get('grant_type') ?? null;
        if(grantType?.includes('authorization_code')) {
          sessionDispatch.tokenExchangeFailed(createLoginError(err.error));
        } else {
          sessionDispatch.tokenRefreshFailed(createLoginError(err.error));
        }

        return throwError(() => err);
      } else if(req.url.includes(authService.userInfoEndpoint) && err.status !== 401) {
        errorService.showError(err.status, err.url, err.error);
        accessDispatch.revoked(ErrorCodeEnum.FORBIDDEN);

        return throwError(() => err);
      } else if(!req.url.includes(authService.logoutEndpoint) && err.status === 401) {
        if(isRefreshTokenInProgress) {
          return refreshToken$.pipe(
            filter((tokenData): tokenData is ITokenData => !!tokenData),
            take(1),
            switchMap(({ idToken: newIdToken, accessToken: newAccessToken }) => next(updateRequest(req, configService.config, newIdToken, newAccessToken)).pipe(
              catchError((err: HttpErrorResponse) => {
                if(err.status === 401) {
                  // The body is a backend error, not an OAuth one: a session that the BE still
                  // rejects after a successful refresh is expired, force the fallback copy.
                  authPageDispatch.loginRedirectRequested(createLoginError(SESSION_EXPIRED_ERROR));
                } else {
                  errorService.showError(err.status, err.url, err.error);
                  if(err.url?.includes(authService.userInfoEndpoint)) {
                    accessDispatch.revoked(ErrorCodeEnum.FORBIDDEN);
                  }
                }

                return throwError(() => err);
              })
            ))
          );
        } else {
          isRefreshTokenInProgress = true;
          refreshToken$.next(null);

          return authService.refreshToken().pipe(
            catchError((err: NServicesHttpErrorResponse) => {
              isRefreshTokenInProgress = false;
              refreshToken$.complete();

              return throwError(() => err);
            }),
            switchMap(({ id_token: newIdToken, access_token: newAccessToken, refresh_token: newRefreshToken }) => {
              isRefreshTokenInProgress = false;
              const tokenData: ITokenData = {
                idToken: newIdToken ?? '',
                accessToken: newAccessToken ?? '',
                refreshToken: newRefreshToken ?? ''
              };
              refreshToken$.next(tokenData);

              sessionDispatch.tokenRefreshed(tokenData);

              return next(updateRequest(req, configService.config, tokenData.idToken, tokenData.accessToken)).pipe(
                catchError((err: HttpErrorResponse) => {
                  if(err.status === 401) {
                    // The body is a backend error, not an OAuth one: a session that the BE still
                    // rejects after a successful refresh is expired, force the fallback copy.
                    authPageDispatch.loginRedirectRequested(createLoginError(SESSION_EXPIRED_ERROR));
                  } else {
                    errorService.showError(err.status, err.url, err.error);
                    if(err.url?.includes(authService.userInfoEndpoint)) {
                      accessDispatch.revoked(ErrorCodeEnum.FORBIDDEN);
                    }
                  }

                  return throwError(() => err);
                })
              );
            })
          );
        }
      } else {
        errorService.showError(err.status, err.url, err.error);
        return throwError(() => err);
      }
    }),
    finalize(() => spinnerService.hide())
  );
};

const isAssets = (url: string): boolean => {
  return url.includes('assets');
};

const isAuth = (url: string, config: IConfig): boolean => {
  return url.includes(config.auth?.issuer ?? '');
};

const isBe = (url: string, config: IConfig): boolean => {
  return !isAssets(url) && !isAuth(url, config);
};

const getApiBaseUrl = (url: string, config: IConfig): string => {
  return isBe(url, config) ? config.apiBaseUrl ?? '' : '';
};

const buildRequest = (request: HttpRequest<unknown>, config: IConfig, email: string | null | undefined, idToken: string | null | undefined, accessToken: string | null | undefined): HttpRequest<unknown> => {
  const skipToken: boolean = request.context.get(SKIP_TOKEN);

  let headers: HttpHeaders = request.headers;

  if(!skipToken) {
    if(isBe(request.url, config)) {
      if(!!idToken) headers = headers.set(AUTHORIZATION, `Bearer ${idToken}`);

      if(!!accessToken) headers = headers.set(ACCESS_TOKEN, accessToken);
      if(!!email) headers = headers.set(USERNAME, email);
    }
  }

  let url: string = `${getApiBaseUrl(request.url, config)}${request.url}`;
  if(JSON_PATTERN.test(request.url)) {
    url += `?v=${new Date().getTime()}`
  }

  return request.clone({
    url,
    headers
  });
};

const updateRequest = (request: HttpRequest<unknown>, config: IConfig, idToken: string | null | undefined, accessToken: string | null | undefined): HttpRequest<unknown> => {
  let headers: HttpHeaders = request.headers;
  if(isBe(request.url, config)) {
    if(!!idToken) headers = headers.set(AUTHORIZATION, `Bearer ${idToken}`);
    if(!!accessToken) headers = headers.set(ACCESS_TOKEN, accessToken);
  }

  return request.clone({ headers });
};

const isBlobError = (err: HttpErrorResponse): boolean => {
  return err.error instanceof Blob && err.error?.type === 'application/json';
};

const parseBlobError = (err: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
  const reader: FileReader = new FileReader();
  const obs$: Observable<HttpEvent<unknown>> = new Observable((observer) => {
    reader.onload = () => {
      observer.error(new HttpErrorResponse({
        headers: err.headers,
        status: err.status,
        statusText: err.statusText,
        url: err.url ?? undefined,
        error: isJson(reader.result as string ?? '') ? JSON.parse(reader.result as string ?? '') : {}
      }));
    };
    reader.onerror = () => observer.error(err);
  });
  reader.readAsText(err.error);
  return obs$;
};

const isJson = (value: string): boolean => {
  try {
    JSON.parse(value);
    return true;
  } catch(e) {
    return false;
  }
};
